import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, ShoppingCart, Award, Play, X } from 'lucide-react';
import { VideoModal } from '@/components/VideoModal';
import { PurchaseButton } from '@/components/CheckoutModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ANIMATION_VIDEO_URL = "https://customer-assets.emergentagent.com/job_6a4505e5-3f70-4447-9214-c7fe0572ffa6/artifacts/4iq7wpjp_Jason%20Visits%20Museum%20Final%20Animation%20.mp4";

const BookCard = ({ book, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    className="group"
    data-testid={`book-card-${book.id}`}
  >
    <div className="relative rounded-2xl overflow-hidden glass-light card-hover">
      {/* Book Cover */}
      <div className="aspect-[3/4] relative overflow-hidden bg-muted">
        <img
          src={book.cover_image}
          alt={book.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
        
        {/* Price Badge */}
        {book.price && (
          <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-cta text-background font-semibold text-sm">
            {book.price}
          </div>
        )}
      </div>
      
      {/* Book Info */}
      <div className="p-6">
        <h3 className="text-xl font-serif font-semibold text-white mb-2 line-clamp-2">
          {book.title}
        </h3>
        <p className="text-sm text-secondary mb-3">{book.author}</p>
        <p className="text-sm text-muted-foreground mb-6 line-clamp-4">
          {book.description}
        </p>
        
        <div className="space-y-3">
          <PurchaseButton
            item={book}
            itemType="book"
            className="w-full flex items-center justify-center gap-2"
          />
          {book.purchase_link && (
            <a
              href={book.purchase_link}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary w-full flex items-center justify-center gap-2 text-center text-sm"
              data-testid={`book-amazon-${book.id}`}
            >
              Buy on Amazon
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
    </div>
  </motion.div>
);

export default function BooksPage() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAnimation, setShowAnimation] = useState(false);
  const previewRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/books`)
      .then(res => {
        setBooks(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  return (
    <div className="min-h-screen pt-24" data-testid="books-page">
      {/* Hero Section */}
      <section className="py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6" data-testid="books-title">
              Jason The Author
            </h1>
            <p className="text-xl text-muted-foreground mb-4">
              Jason has written many books that have been inspirational to all.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Animation Section */}
      <section className="py-8 pb-16" data-testid="featured-animation-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto"
          >
            <div className="rounded-2xl overflow-hidden glass-light">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                {/* Video Preview */}
                <div
                  className="relative aspect-video lg:aspect-auto lg:min-h-[320px] cursor-pointer group overflow-hidden"
                  onClick={() => setShowAnimation(true)}
                  data-testid="animation-preview"
                >
                  <video
                    ref={previewRef}
                    className="w-full h-full object-cover"
                    muted
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={(e) => { e.target.currentTime = 2; }}
                  >
                    <source src={ANIMATION_VIDEO_URL} type="video/mp4" />
                  </video>
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className="w-20 h-20 rounded-full bg-cta/90 flex items-center justify-center shadow-lg shadow-cta/30"
                    >
                      <Play className="w-10 h-10 text-background ml-1" fill="currentColor" />
                    </motion.div>
                  </div>
                </div>

                {/* Info */}
                <div className="p-8 flex flex-col justify-center">
                  <span className="text-cta text-sm font-semibold tracking-wider uppercase mb-3">
                    Featured Animation
                  </span>
                  <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-4">
                    Jason Visits The Museum
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Watch the animated adaptation of the beloved children's book. Join Jason on his quest for knowledge as he visits The Museum featuring the King Tut and Black History exhibit, overcoming challenges with his newly discovered friends.
                  </p>
                  <button
                    onClick={() => setShowAnimation(true)}
                    className="btn-primary inline-flex items-center gap-2 w-fit"
                    data-testid="watch-animation-btn"
                  >
                    <Play className="w-4 h-4" />
                    Watch Animation
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Books Grid */}
      <section className="py-8">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-2xl glass-light animate-pulse">
                  <div className="aspect-[3/4] bg-muted" />
                  <div className="p-6 space-y-4">
                    <div className="h-6 bg-muted rounded" />
                    <div className="h-4 bg-muted rounded w-1/2" />
                    <div className="h-20 bg-muted rounded" />
                    <div className="h-12 bg-muted rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : books.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {books.map((book, index) => (
                <BookCard key={book.id} book={book} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-muted-foreground">No books available yet. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* Award Recognition Section */}
      <section className="py-16 bg-card/50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="p-8 rounded-2xl glass-light">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-full bg-cta/20 flex items-center justify-center">
                  <Award className="w-7 h-7 text-cta" />
                </div>
                <h2 className="text-2xl font-serif font-bold text-white">
                  Black Excellence Individual Achievement Award
                </h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Jason E. Williams was Awarded a Black Excellence Individual Achievement Award Certificate of Recognition by San Bernardino County and 1st District Supervisor Joe Baca, Jr. for his Community outreach and Children's Book series.
              </p>
              
              {/* Award Images */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <img 
                  src="https://images.squarespace-cdn.com/content/v1/61c0b1b4079e2175348a6a52/1656618549274-FCN3561PLSBC5KPBBWUE/DSC_0884.JPG" 
                  alt="Award Ceremony" 
                  className="rounded-lg w-full h-48 object-cover"
                />
                <img 
                  src="https://images.squarespace-cdn.com/content/v1/61c0b1b4079e2175348a6a52/1656618549683-DTEKX0MCRH3STKRLFY5V/DSC_0886.JPG" 
                  alt="Award Ceremony" 
                  className="rounded-lg w-full h-48 object-cover"
                />
                <img 
                  src="https://images.squarespace-cdn.com/content/v1/61c0b1b4079e2175348a6a52/1656618553275-YPD82RIP2RXPCPK7GG8C/DSC_0896.JPG" 
                  alt="Award Ceremony" 
                  className="rounded-lg w-full h-48 object-cover"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Institutional Inquiry */}
      <section className="py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl glass-light text-center max-w-3xl mx-auto"
          >
            <h2 className="text-2xl font-serif font-bold text-white mb-4">
              Institutional Orders
            </h2>
            <p className="text-muted-foreground mb-6">
              Looking to order books for your institution, classroom, or organization? Contact us for bulk pricing and special arrangements.
            </p>
            <a
              href="/contact"
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="institutional-inquiry"
            >
              Contact for Institutional Orders
            </a>
          </motion.div>
        </div>
      </section>

      {/* Animation Video Modal */}
      <VideoModal
        isOpen={showAnimation}
        onClose={() => setShowAnimation(false)}
        videoUrl={ANIMATION_VIDEO_URL}
        title="Jason Visits The Museum - Animation"
      />
    </div>
  );
}
